export type AnimalEntry = {
	animal: string;
	imageUrl: string;
};

type AnimalListResponse = {
	animals: AnimalEntry[];
	total: number;
};

const fallbackImageVersion = "a60026c088dc0dee";

const fallbackAnimalSlugs = [
	"albatross",
	"alligator",
	"alpaca",
	"anaconda",
	"anteater",
	"antelope",
	"armadillo",
	"baboon",
	"badger",
	"bear",
	"beaver",
	"bison",
	"boar",
	"buffalo",
	"camel",
	"capybara",
	"cardinal",
	"cat",
	"chameleon",
	"cheetah",
	"chicken",
	"chimpanzee",
	"chinchilla",
	"chipmunk",
	"cobra",
	"cockatoo",
	"cougar",
	"cow",
	"coyote",
	"crow",
	"deer",
	"dog",
	"duck",
	"eagle",
	"elephant",
	"elk",
	"falcon",
	"ferret",
	"flamingo",
	"fox",
	"gecko",
	"giraffe",
	"goose",
	"gorilla",
	"groundhog",
	"hedgehog",
	"hippo",
	"horse",
	"hummingbird",
	"jaguar",
	"kangaroo",
	"koala",
	"leopard",
	"lion",
	"llama",
	"meerkat",
	"monkey",
	"moose",
	"newt",
	"ocelot",
	"opossum",
	"orangutan",
	"owl",
	"panda",
	"panther",
	"penguin",
	"polar-bear",
	"rabbit",
	"ram",
	"raven",
	"rhino",
	"salamander",
	"skunk",
	"sloth",
	"sparrow",
	"tiger",
	"turkey",
	"wolf",
	"yak",
	"zebra",
];

const animalFilterGroups = {
	birds: new Set([
		"albatross",
		"cardinal",
		"chicken",
		"cockatoo",
		"crow",
		"duck",
		"eagle",
		"falcon",
		"goose",
		"hummingbird",
		"owl",
		"sparrow",
		"turkey",
	]),
	cats: new Set([
		"cat",
		"cheetah",
		"cougar",
		"leopard",
		"ocelot",
		"panther",
		"tiger",
	]),
	big: new Set([
		"bear",
		"bison",
		"buffalo",
		"elephant",
		"gorilla",
		"hippo",
		"horse",
		"polar-bear",
		"rhino",
		"yak",
		"camel",
		"cow",
		"giraffe",
	]),
	reptiles: new Set([
		"alligator",
		"anaconda",
		"chameleon",
		"cobra",
		"gecko",
		"newt",
		"salamander",
	]),
};

export const animalFilters = [
	{ id: "all", label: "All" },
	{ id: "birds", label: "Birds" },
	{ id: "cats", label: "Cats" },
	{ id: "big", label: "Big" },
	{ id: "reptiles", label: "Reptiles" },
] as const;

export type AnimalFilterId = (typeof animalFilters)[number]["id"];

/**
 * Build the stable CDN URL used when the API is unavailable during first paint.
 */
export const buildAnimalImageUrl = (slug: string) => {
	return `https://images.avataranimals.com/animals/transparent/${slug}.webp?v=${fallbackImageVersion}`;
};

export const fallbackAnimals = fallbackAnimalSlugs.map((animal) => ({
	animal,
	imageUrl: buildAnimalImageUrl(animal),
}));

export const knownAnimalCount = fallbackAnimals.length;

/**
 * Convert slug text into the lowercase display style used by the reference art.
 */
export const formatAnimalName = (slug: string) => {
	return slug.replaceAll("-", " ");
};

/**
 * Convert slug text into a title-cased label for alt text and ARIA labels.
 */
export const formatAnimalLabel = (slug: string) => {
	return formatAnimalName(slug)
		.split(" ")
		.map((segment) =>
			segment.length > 0
				? `${segment[0]?.toUpperCase()}${segment.slice(1)}`
				: segment,
		)
		.join(" ");
};

/**
 * Find an animal by slug without forcing every caller to repeat null checks.
 */
export const findAnimalBySlug = (
	animals: AnimalEntry[],
	slug: string | null,
) => {
	if (!slug) {
		return null;
	}

	return animals.find((animal) => animal.animal === slug) ?? null;
};

/**
 * Pick one animal from a list for the spotlight interaction.
 */
export const getRandomAnimal = (animals: AnimalEntry[]) => {
	if (animals.length === 0) {
		return null;
	}

	return animals[Math.floor(Math.random() * animals.length)] ?? null;
};

/**
 * Apply the gallery chip and search filters as render-time derivation.
 */
export const filterAnimals = (
	animals: AnimalEntry[],
	filterId: AnimalFilterId,
	query: string,
) => {
	const group = filterId === "all" ? null : animalFilterGroups[filterId];
	const normalizedQuery = query.trim().toLowerCase();

	return animals.filter((animal) => {
		if (group && !group.has(animal.animal)) {
			return false;
		}

		if (!normalizedQuery) {
			return true;
		}

		return formatAnimalName(animal.animal).includes(normalizedQuery);
	});
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
	return typeof value === "object" && value !== null;
};

const isAnimalEntry = (value: unknown): value is AnimalEntry => {
	return (
		isRecord(value) &&
		typeof value.animal === "string" &&
		typeof value.imageUrl === "string"
	);
};

const isAnimalListResponse = (value: unknown): value is AnimalListResponse => {
	return (
		isRecord(value) &&
		Array.isArray(value.animals) &&
		value.animals.every(isAnimalEntry) &&
		typeof value.total === "number"
	);
};

/**
 * Fetch the live animal index while allowing callers to keep rendering fallbacks.
 */
export const fetchAnimals = async () => {
	const response = await fetch("/api/animals", {
		cache: "no-store",
	});

	if (!response.ok) {
		throw new Error("Could not load animals.");
	}

	const payload: unknown = await response.json();
	if (!isAnimalListResponse(payload)) {
		throw new Error("Animal index response was not valid.");
	}

	return payload.animals;
};
